import { useState } from "react";
import { toast } from "sonner";
import { Palette, Sparkles, Check, Droplet } from "lucide-react";
import { detectProductColors, updatePhotoColors } from "../lib/api";

/**
 * Admin editor for a product's detected primary colours.
 * Rename a colour, merge two by giving them the same name, or mark Uncoloured.
 */
export default function ColorVariantEditor({ favorite, onUpdated, rank }) {
  const initial = favorite.product?.photo_colors || [];
  const [rows, setRows] = useState(initial);
  const [detecting, setDetecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const distinct = [...new Set(rows.map((r) => r.color_name).filter(Boolean))];

  const setRow = (i, patch) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const handleDetect = async () => {
    if (detecting) return;
    setDetecting(true);
    try {
      const res = await detectProductColors(favorite.id);
      setRows(res.photo_colors || []);
      setOpen(true);
      toast.success("Colours detected", {
        description: [...new Set((res.photo_colors || []).map((c) => c.color_name))].join(", "),
      });
      onUpdated?.({ ...favorite, product: { ...favorite.product, photo_colors: res.photo_colors, colors_detected: true } });
    } catch (e) {
      toast.error("Detection failed", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setDetecting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updatePhotoColors(favorite.id, rows);
      toast.success("Colours saved");
      onUpdated?.({ ...favorite, product: { ...favorite.product, photo_colors: res.photo_colors, colors_detected: true } });
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 border-t border-zinc-200 pt-3" data-testid={`color-editor-${rank}`}>
      <div className="flex items-center justify-between gap-2">
        <button
          onClick={() => setOpen((o) => !o)}
          className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 hover:text-[#002FA7] inline-flex items-center gap-1"
          data-testid={`toggle-colors-${rank}`}
        >
          <Palette size={11} strokeWidth={2.5} />
          Colours{distinct.length ? ` · ${distinct.length}` : ""}
        </button>
        <button
          onClick={handleDetect}
          disabled={detecting}
          data-testid={`detect-colors-${rank}`}
          className="text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1 px-2 py-1 border border-zinc-900 hover:bg-zinc-900 hover:text-white transition disabled:opacity-50"
          title="Re-scan photos with AI to detect candle colours"
        >
          <Sparkles size={11} strokeWidth={2.5} className={detecting ? "animate-pulse" : ""} />
          {detecting ? "Detecting..." : "Detect"}
        </button>
      </div>

      {open && (
        <div className="mt-3 space-y-2" data-testid={`color-rows-${rank}`}>
          {rows.length === 0 && (
            <p className="text-xs text-zinc-500">
              No colours yet. Tap <b>Detect</b> to read them from the photos.
            </p>
          )}
          {rows.map((r, i) => (
            <div key={r.image_url + i} className="flex items-center gap-2">
              <img src={r.image_url} alt="" className="w-9 h-9 object-cover bg-zinc-100 border border-zinc-200 shrink-0" />
              <span
                className="w-4 h-4 rounded-full border border-zinc-300 shrink-0"
                style={{ backgroundColor: r.hex || "#ccc" }}
                aria-hidden
              />
              <input
                type="text"
                value={r.color_name}
                onChange={(e) => setRow(i, { color_name: e.target.value })}
                data-testid={`color-name-input-${rank}-${i}`}
                className="flex-1 min-w-0 px-2 py-1 text-[11px] font-mono border border-zinc-300 focus:outline-none focus:border-[#002FA7]"
                placeholder="Colour name"
              />
              <button
                onClick={() => setRow(i, { color_name: "Uncoloured", uncoloured: true, hex: "#EFE9DE" })}
                data-testid={`uncoloured-btn-${rank}-${i}`}
                className="text-[9px] uppercase tracking-wider font-bold px-2 py-1 border border-zinc-300 hover:border-zinc-900 inline-flex items-center gap-1 shrink-0"
                title="Mark as natural / no dye"
              >
                <Droplet size={10} strokeWidth={2.5} /> Uncoloured
              </button>
            </div>
          ))}
          {rows.length > 0 && (
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid={`save-colors-${rank}`}
              className="w-full bg-[#002FA7] text-white text-[10px] uppercase tracking-widest font-bold py-2 px-3 inline-flex items-center justify-center gap-1 hover:bg-[#001f70] disabled:opacity-50"
            >
              <Check size={12} strokeWidth={3} /> Save colours
            </button>
          )}
        </div>
      )}
    </div>
  );
}
